import {
  AnalyticsFilterSelection,
  AnalyticsFilterState,
} from 'pages/ReportsPage/store/analyticsFilter.store.types';
import { ISO8601TupleType } from 'types/common.types';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';
import { DEFAULT_DATE_PRESET } from 'pages/ReportsPage/components/AnalyticsDashboards/FilterBar/FilterBar.constants';
import { isSameDayRange } from 'pages/ReportsPage/components/AnalyticsDashboards/FilterBar/PayPeriodDateInput.utils';
import { LocationOption } from 'pages/ReportsPage/components/AnalyticsDashboards/FilterBar/useFilterBarLocationData';

export const getDefaultLocationId = (locationOptions: LocationOption[]): string | undefined => {
  const currentLocationValue = getCurrentlySelectedLocation()?.value;
  const currentLocationOption = currentLocationValue
    ? locationOptions.find((opt) => opt.value === String(currentLocationValue))
    : undefined;
  return (currentLocationOption ?? locationOptions[0])?.value;
};

const areIdSetsEqual = (a: string[], b: string[]): boolean => {
  const aSet = new Set(a);
  const bSet = new Set(b);
  return aSet.size === bSet.size && Array.from(aSet).every((id) => bSet.has(id));
};

const isEntireCatalogSelected = (
  selection: AnalyticsFilterSelection,
  allIds: string[] | null,
): boolean =>
  selection === null || (!!allIds && allIds.length > 0 && areIdSetsEqual(selection, allIds));

export function isDefaultFilterSelection(
  selection: Pick<
    AnalyticsFilterState,
    'locationIds' | 'positions' | 'employmentTypes' | 'datePreset'
  >,
  catalog: { allLocationIds: string[]; allPositionIds: string[] | null },
): boolean {
  return (
    selection.employmentTypes.length === 0 &&
    selection.datePreset === DEFAULT_DATE_PRESET &&
    isEntireCatalogSelected(selection.locationIds, catalog.allLocationIds) &&
    isEntireCatalogSelected(selection.positions, catalog.allPositionIds)
  );
}

export function isDefaultPayPeriodFilterSelection(
  selection: Pick<
    AnalyticsFilterState,
    'locationIds' | 'positions' | 'employmentTypes' | 'dateRange' | 'defaultPositionId'
  >,
  baseline: {
    defaultLocationId: string | undefined;
    payPeriodCommunityId: string | null;
    payPeriodPositionId: string | null;
    currentPeriodRange: ISO8601TupleType | null;
  },
): boolean {
  const { locationIds, positions, employmentTypes, dateRange, defaultPositionId } = selection;
  const { defaultLocationId, payPeriodCommunityId, payPeriodPositionId, currentPeriodRange } =
    baseline;
  return (
    employmentTypes.length === 0 &&
    locationIds?.length === 1 &&
    defaultLocationId !== undefined &&
    locationIds[0] === defaultLocationId &&
    positions?.length === 1 &&
    defaultPositionId !== null &&
    positions[0] === defaultPositionId &&
    payPeriodCommunityId === locationIds[0] &&
    payPeriodPositionId === positions[0] &&
    currentPeriodRange !== null &&
    isSameDayRange(dateRange, currentPeriodRange)
  );
}
